// POS refund WebView pages — Backoffice pages under src/pages/webview/ that the TastyPOS app embeds in a
// WebView to run a refund. Keep in sync with the /…OrderRefund routes in router.js (all marked "used in POS").
const POS_WEBVIEW_PATHS = new Set([
  '/webOrderRefund',
  '/softpayOrderRefund',
  '/cashOrderRefund',
  '/cashAndCardOrderRefund',
  '/payMyPartOrderRefund',
  '/vivaOrderRefund',
  '/vivaCashAndCardOrderRefund',
  '/vivaPayMyPartOrderRefund',
]);

// True for the whole life of a POS refund WebView session — not just on the /…OrderRefund routes. The POS
// always ENTERS the WebView on one of those routes, so we latch a sticky flag on first sight and keep
// returning true afterwards. This matters because when a refund completes the page does
// `$router.push('/orderDetail')` — a SHARED page that normal web users also open — so we must NOT key off the
// route there (that would force Basic for real web users too). sessionStorage is scoped to this WebView's
// document session, and a real browser never enters via a /…OrderRefund route, so it never latches the flag.
export function isPosWebviewSession() {
  try {
    if (typeof window === 'undefined') return false;
    if (POS_WEBVIEW_PATHS.has(window.location.pathname)) {
      sessionStorage.setItem('posWebview', '1');
      return true;
    }
    return sessionStorage.getItem('posWebview') === '1';
  } catch (e) {
    return false;
  }
}

export default function authHeader() {
  const user = JSON.parse(localStorage.getItem('user'));
  if (!user || !user.authToken) {
    return {};
  }

  // Normal Backoffice users carry a server-issued session token (minted ONLY after SMS 2FA) → send as Bearer.
  //
  // The TastyPOS refund WebView is the exception: it embeds these Backoffice /…OrderRefund pages and injects a
  // Basic auth token (Base64(username:password)) into localStorage.user, NOT a session token. The POS is an
  // already-provisioned, physically-controlled terminal that authenticates with HTTP Basic on every API call
  // and never performs the interactive 2FA web login. The backend still accepts Basic for ADMIN/EMPLOYEE —
  // only SUPER_ADMIN is forced onto a session token (see WebSecurityConfig.enforcedRoles) — so Basic stays
  // authenticated on these pages. Without this, the POS token would go out as `Bearer Base64(user:pass)`, fail
  // session-token validation → 401 → the response interceptor bounces the WebView to /login on an endless loop
  // (refunds impossible). This regressed when login switched Basic→Bearer for 2FA (commit b78d3a2).
  //
  // Detected two ways so it works for BOTH already-deployed POS builds (latched from the entry webview route,
  // and kept for the whole session incl. the post-refund /orderDetail page) and future builds (which
  // additionally stamp authScheme:'Basic' on the injected user).
  const useBasic = user.authScheme === 'Basic' || isPosWebviewSession();
  return { Authorization: (useBasic ? 'Basic ' : 'Bearer ') + user.authToken };
}
